import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  addOvertime, assignShift, decideOvertime, loadDirectory, loadOvertime, loadShiftAssignments, loadShifts, saveShift, suggestOvertime,
  type OtKind, type OvertimeEntry, type Shift, type ShiftAssignment,
} from "../data";
import type { DirectoryRow, Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hours = (m: number) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
export const otTone: Record<OvertimeEntry["status"], string> = { suggested: "warning", approved: "success", rejected: "danger" };
const KINDS: OtKind[] = ["day", "night", "rest_day", "holiday"];

/** Shifts and overtime: review overtime found in attendance, record it by hand, and manage shifts. */
export function Overtime({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<"overtime" | "shifts">("overtime");
  return (
    <div className="page">
      <div className="tabs" role="tablist">
        {([["overtime", "otTab"], ["shifts", "shiftsTab"]] as ["overtime" | "shifts", StringKey][]).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "tab current" : "tab"} onClick={() => setTab(k)}>{t(label)}</button>
        ))}
      </div>
      {tab === "overtime" ? <OvertimeTab ws={ws} onOpen={onOpen} /> : <Shifts ws={ws} />}
    </div>
  );
}

function OvertimeTab({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const canManage = ws.permissions.has("attendance.manage");
  const [month, setMonth] = useState(iso(new Date()).slice(0, 7));
  const [rows, setRows] = useState<OvertimeEntry[] | null>(null);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<OvertimeEntry["status"] | "all">("suggested");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const from = `${month}-01`;
  const to = iso(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0));
  const refresh = () => loadOvertime(cid, from, to).then((r) => { setRows(r); setSel(new Set()); }).catch((e) => setMsg({ ok: false, text: e.message }));
  useEffect(() => { void refresh(); }, [cid, month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { void loadDirectory(cid).then(setPeople); }, [cid]);
  const name = (id: string) => { const p = people.find((x) => x.employment_id === id); return p ? pick(p.full_name, p.full_name_ar) : "—"; };
  const shown = (rows ?? []).filter((r) => filter === "all" || r.status === filter);
  const count = (s: OvertimeEntry["status"]) => (rows ?? []).filter((r) => r.status === s).length;
  const totals = useMemo(() => {
    const m = new Map<OtKind, number>();
    for (const r of rows ?? []) if (r.status === "approved") m.set(r.kind, (m.get(r.kind) ?? 0) + r.minutes);
    return m;
  }, [rows]);

  async function act(fn: () => Promise<unknown>, done: (n: unknown) => string) {
    setBusy(true);
    setMsg(null);
    try { const n = await fn(); await refresh(); setMsg({ ok: true, text: done(n) }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
    finally { setBusy(false); }
  }
  const decidable = shown.filter((r) => !r.payroll_run_id);
  const toggleAll = () => setSel(sel.size === decidable.length ? new Set() : new Set(decidable.map((r) => r.id)));

  return (
    <>
      <section className="card">
        <div className="row spread">
          <input type="month" aria-label={t("dashMonth")} value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          {canManage && <button type="button" className="btn primary" disabled={busy} onClick={() => act(() => suggestOvertime(cid, from, to), (n) => t("otFound").replace("{n}", String(n)))}>{t("otFind")}</button>}
        </div>
        <p className="muted small-note">{t("otHelp")}</p>
        <div className="kpis">
          {KINDS.map((k) => <div key={k} className="stat"><span className="stat-label">{t(`ot_${k}` as StringKey)}</span><span className="stat-value small mono">{hours(totals.get(k) ?? 0)}</span><span className="stat-sub">{t("otApprovedHours")}</span></div>)}
        </div>
        {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      </section>
      <section className="card">
        <div className="filter-chips">
          {(["suggested", "approved", "rejected", "all"] as const).map((s) => (
            <button key={s} type="button" className={`chip${filter === s ? " current" : ""}`} aria-pressed={filter === s} onClick={() => { setFilter(s); setSel(new Set()); }}>
              {s === "all" ? t("alertsAll") : t(`otStatus_${s}` as StringKey)}{s !== "all" ? ` · ${count(s)}` : ""}
            </button>
          ))}
        </div>
        {decidable.length > 0 && (
          <div className="row">
            <label className="check"><input type="checkbox" checked={sel.size > 0 && sel.size === decidable.length} onChange={toggleAll} /> {t("otSelectAll")}</label>
            <button type="button" className="btn primary small" disabled={busy || sel.size === 0} onClick={() => act(() => decideOvertime([...sel], true), (n) => `${n} ${t("otApprovedN")}`)}>{t("approve")} ({sel.size})</button>
            <button type="button" className="btn secondary small" disabled={busy || sel.size === 0} onClick={() => act(() => decideOvertime([...sel], false), (n) => `${n} ${t("otRejectedN")}`)}>{t("reject")}</button>
          </div>
        )}
        {rows === null ? <p className="muted">{t("loading")}</p> : shown.length === 0 ? <p className="muted">{t("otNone")}</p> : (
          <div className="table-wrap"><table>
            <thead><tr><th /><th>{t("employee")}</th><th>{t("date")}</th><th>{t("otKind")}</th><th>{t("otHours")}</th><th className="hide-sm">{t("status")}</th></tr></thead>
            <tbody>{shown.map((r) => (
              <tr key={r.id}>
                <td>{!r.payroll_run_id && <input type="checkbox" aria-label={t("otSelect")} checked={sel.has(r.id)}
                  onChange={(e) => { const s = new Set(sel); if (e.target.checked) s.add(r.id); else s.delete(r.id); setSel(s); }} />}</td>
                <td><button type="button" className="link" onClick={() => onOpen(r.employment_id)}>{name(r.employment_id)}</button>{r.note && <span className="muted small-note"> · {r.note}</span>}</td>
                <td className="mono">{r.work_date}</td>
                <td>{t(`ot_${r.kind}` as StringKey)}</td>
                <td className="mono">{hours(r.minutes)}</td>
                <td className="hide-sm"><span className={`badge ${otTone[r.status]}`}>{r.payroll_run_id ? t("otPaid") : t(`otStatus_${r.status}` as StringKey)}</span></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </section>
      <AddOvertime people={people} onAdded={refresh} canManage={canManage} />
    </>
  );
}

function AddOvertime({ people, onAdded, canManage }: { people: DirectoryRow[]; onAdded: () => Promise<void> | void; canManage: boolean }) {
  const { t, pick } = useI18n();
  const [f, setF] = useState({ emp: "", date: iso(new Date()), kind: "day" as OtKind, hours: "1", note: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  async function add(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      await addOvertime(f.emp, f.date, f.kind, Math.round(Number(f.hours) * 60), f.note);
      setMsg({ ok: true, text: canManage ? t("otAdded") : t("otSent") });
      await onAdded();
    } catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }
  return (
    <form className="card" onSubmit={add}>
      <h2>{t("otAdd")}</h2>
      <div className="field-row">
        <label className="field"><span>{t("employee")}</span>
          <select value={f.emp} onChange={(e) => setF({ ...f, emp: e.target.value })} required>
            <option value="">—</option>
            {people.filter((p) => p.status !== "terminated" && p.status !== "starting").map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)}</option>)}
          </select></label>
        <label className="field"><span>{t("date")}</span><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} required /></label>
        <label className="field"><span>{t("otKind")}</span>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as OtKind })}>
            {KINDS.map((k) => <option key={k} value={k}>{t(`ot_${k}` as StringKey)}</option>)}
          </select></label>
        <label className="field"><span>{t("otHours")}</span><input type="number" min="0.25" max="24" step="0.25" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} required /></label>
        <label className="field"><span>{t("note")}</span><input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></label>
      </div>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      <div className="actions"><button className="btn secondary">{t("payAdd")}</button></div>
      <p className="muted small-note">{t("otRatesNote")}</p>
    </form>
  );
}

function Shifts({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const canManage = ws.permissions.has("attendance.manage");
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [assigns, setAssigns] = useState<ShiftAssignment[]>([]);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [f, setF] = useState({ name: "", name_ar: "", start: "09:00", end: "17:00", brk: "60" });
  const [a, setA] = useState({ shift: "", from: iso(new Date()), sel: new Set<string>() });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = async () => {
    const [s, as, p] = await Promise.all([loadShifts(cid), loadShiftAssignments(cid), loadDirectory(cid)]);
    setShifts(s); setAssigns(as); setPeople(p.filter((x) => x.status !== "terminated"));
  };
  useEffect(() => { void refresh(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const today = iso(new Date());
  const currentShift = (emp: string) => {
    const x = assigns.find((s) => s.employment_id === emp && s.start_date <= today && (!s.end_date || s.end_date >= today));
    return x ? shifts.find((s) => s.id === x.shift_id) : undefined;
  };
  const onShift = (id: string) => assigns.filter((s) => s.shift_id === id && s.start_date <= today && (!s.end_date || s.end_date >= today)).length;

  async function addShift(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      await saveShift(cid, { name: f.name, name_ar: f.name_ar || null, start_time: f.start, end_time: f.end, break_minutes: Number(f.brk) });
      setF({ name: "", name_ar: "", start: "09:00", end: "17:00", brk: "60" });
      await refresh();
    } catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }
  async function assign(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      const n = await assignShift([...a.sel], a.shift || null, a.from);
      setMsg({ ok: true, text: t("shiftAssigned").replace("{n}", String(n)) });
      setA({ ...a, sel: new Set() });
      await refresh();
    } catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }

  return (
    <>
      <section className="card">
        <h2>{t("shiftsTab")}</h2>
        {shifts.length === 0 ? <p className="muted">{t("shiftNone")}</p> : (
          <ul className="req-list">
            {shifts.map((s) => (
              <li key={s.id}>
                <span><strong>{pick(s.name, s.name_ar)}</strong> · <span className="mono">{s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)}</span>
                  {s.break_minutes ? <span className="muted small-note"> · {t("shiftBreak")} {s.break_minutes} min</span> : null}
                  <span className="muted small-note"> · {onShift(s.id)} {t("payPeople").toLowerCase()}</span></span>
                {canManage ? (
                  <label className="check"><input type="checkbox" checked={s.active} onChange={async (e) => { await saveShift(cid, { id: s.id, active: e.target.checked }); await refresh(); }} /> {t("active")}</label>
                ) : !s.active && <span className="badge neutral">{t("inactive")}</span>}
              </li>
            ))}
          </ul>
        )}
        <p className="muted small-note">{t("shiftHelp")}</p>
      </section>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {canManage && (
        <>
          <form className="card" onSubmit={assign}>
            <h2>{t("shiftAssign")}</h2>
            <div className="field-row">
              <label className="field"><span>{t("shiftOne")}</span>
                <select value={a.shift} onChange={(e) => setA({ ...a, shift: e.target.value })}>
                  <option value="">{t("shiftCompanyHours")}</option>
                  {shifts.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{pick(s.name, s.name_ar)} ({s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)})</option>)}
                </select></label>
              <label className="field"><span>{t("payFrom")}</span><input type="date" value={a.from} onChange={(e) => setA({ ...a, from: e.target.value })} required /></label>
            </div>
            <div className="pick-list">
              {people.map((p) => {
                const cur = currentShift(p.employment_id);
                return (
                  <label key={p.employment_id} className="check">
                    <input type="checkbox" checked={a.sel.has(p.employment_id)} onChange={(e) => { const s = new Set(a.sel); if (e.target.checked) s.add(p.employment_id); else s.delete(p.employment_id); setA({ ...a, sel: s }); }} />
                    {pick(p.full_name, p.full_name_ar)} <span className="muted small-note">{cur ? pick(cur.name, cur.name_ar) : t("shiftCompanyHours")}</span>
                  </label>
                );
              })}
            </div>
            <div className="actions"><button className="btn primary" disabled={a.sel.size === 0}>{t("shiftAssignBtn")} ({a.sel.size})</button></div>
          </form>
          <form className="card" onSubmit={addShift}>
            <h2>{t("shiftNew")}</h2>
            <div className="field-row">
              <label className="field"><span>{t("nameEn")}</span><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></label>
              <label className="field"><span>{t("nameAr")}</span><input dir="rtl" value={f.name_ar} onChange={(e) => setF({ ...f, name_ar: e.target.value })} /></label>
              <label className="field"><span>{t("shiftStart")}</span><input type="time" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} required /></label>
              <label className="field"><span>{t("shiftEnd")}</span><input type="time" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} required /></label>
              <label className="field"><span>{t("shiftBreak")} (min)</span><input type="number" min="0" max="240" value={f.brk} onChange={(e) => setF({ ...f, brk: e.target.value })} /></label>
            </div>
            <div className="actions"><button className="btn secondary">{t("payAdd")}</button></div>
          </form>
        </>
      )}
    </>
  );
}
